import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import Modal from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import { selectUser } from "../../store/userSlice";
import { VehicleNames } from "../../utils/GeneralTypes";
import { getCompareVehicles, updateCompare } from "../VehiclePage/utils";
import { getComparedVehicles } from "../../utils/fetcher";
import { compareData } from "./utils";
import CompareTable from "../../components/Compare/CompareTable";
import VehicleTypesControllers, { VehicleConditionsControllers } from "../../components/General/VehiclesControllers";
import AddVehicleToCompare from "../../components/Compare/AddVehicleToCompare";
import style from "./index.module.css";
import { useScrollToTop } from "../../utils/generalUtils";
import EmptyContent from "../../components/General/EmptyContent";
import { useTranslation } from "../../hooks/useTranslation";
import { useHistory } from "react-router-dom";

const Compare = ({ vehicleNames, vehicleState }: ComponentPropsType) => {
  const { user } = useSelector(selectUser);
  const { locale } = useTranslation();
  const history = useHistory();

  const initialMount = useRef<boolean>(true);

  useScrollToTop();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: {
      vehicles: any[];
      attributes: any[];
    };
  }>({
    isFetching: true,
    data: {
      vehicles: [],
      attributes: [],
    },
  });

  const [isRemoveModalActive, setIsRemoveModalActive] = useState<number>(-1);
  const [isAddModalActive, setIsAddModalActive] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      history.replace("/sign");
    } else {
      if (!initialMount.current) {
        setComponentData((current) => ({
          ...current,
          isFetching: true,
        }));
      }
      initialMount.current = false;
      const compareVehicles = getCompareVehicles()?.vehicles?.[vehicleState] || [];
      let data: any = {
        vehicles: [],
      };
      (async () => {
        if (compareVehicles.length > 0) {
          await getComparedVehicles(vehicleNames.single, vehicleState, compareVehicles)
            .then((res) => {
              const refinedData = compareData(res.data.data);
              data.vehicles = refinedData.vehicles;
              data.attributes = refinedData.attributes;
            })
            .catch((error) => {
              compareVehicles.forEach((id: string) => {
                updateCompare(vehicleNames.single, vehicleState, id);
              });
            });
        }
        setComponentData({
          isFetching: false,
          data,
        });
      })();
    }
  }, [vehicleNames, vehicleState, user, history]);

  const onRemoveCompareHandler = useCallback(() => {
    updateCompare(vehicleNames.single, vehicleState, isRemoveModalActive.toString());
    setComponentData((currentState) => {
      const removedIndex = currentState.data.vehicles.findIndex(
        (el) => el.id.toString() === isRemoveModalActive.toString(),
      );
      return {
        ...currentState,
        data: {
          ...currentState.data,
          vehicles: currentState.data.vehicles.filter((el) => el.id.toString() !== isRemoveModalActive.toString()),
          attributes: currentState.data.attributes.map((row) =>
            row.filter((_: any, index: number) => index !== removedIndex),
          ),
        },
      };
    });
    setIsRemoveModalActive(-1);
  }, [vehicleNames, vehicleState, isRemoveModalActive]);

  return (
    <>
      {isAddModalActive && (
        <AddVehicleToCompare
          setVehicles={setComponentData}
          setIsAddModalActive={setIsAddModalActive}
          vehicleNames={vehicleNames}
          vehicleState={vehicleState}
        />
      )}
      {isRemoveModalActive !== -1 && (
        <Modal onClose={() => setIsRemoveModalActive(-1)}>
          <div className={style.modal}>
            <p className={style.modal_title}>{locale(`${vehicleNames.single}_sure_to_remove_compare`)}</p>
            <div className={style.modal_ctas}>
              <Button styleType="primary" onClick={onRemoveCompareHandler}>
                {locale("remove")}
              </Button>
              <Button styleType="primary_black" onClick={() => setIsRemoveModalActive(-1)}>
                {locale("cancel")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                {
                  text: locale("compare"),
                  link: "",
                },
                {
                  text: locale(`new_${vehicleNames.plural}`),
                  link: "",
                },
              ]}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <div className={style.section_header}>
              <h3 className="main_section__title">{locale("compare")}</h3>
              <VehicleConditionsControllers baseLink={`/compare/${vehicleNames.plural}`} active={vehicleState} />
              <VehicleTypesControllers baseLink="/compare" postLink="new" active={vehicleNames.single} />
            </div>
            <div className={style.all_vehicles}>
              {componentData.data.vehicles.length === 0 && !componentData.isFetching && (
                <EmptyContent>
                  <p>{locale("no_compare").replace("%vehicles%", locale(vehicleNames.plural))}.</p>
                </EmptyContent>
              )}
              {((componentData.data.vehicles.length > 0 && !componentData.isFetching) || componentData.isFetching) &&
                (componentData.isFetching ? (
                  <LoadingSkeleton count={8} />
                ) : (
                  <CompareTable
                    vehicleNames={vehicleNames}
                    vehicles={componentData.data.vehicles}
                    attributes={componentData.data.attributes}
                    removeVehicle={setIsRemoveModalActive}
                    addVehicle={setIsAddModalActive}
                    vehicleState={vehicleState}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Compare;

type ComponentPropsType = {
  vehicleNames: VehicleNames;
  vehicleState: "new" | "used" | "imported";
};
